import { type FormEvent, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Search } from 'lucide-react'
import AdminNav from '../../components/admin/AdminNav'
import BookingTable from '../../components/admin/BookingTable'
import ReasonModal from '../../components/admin/ReasonModal'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import { useLang } from '../../contexts/LangContext'
import {
  ADMIN_BOOKINGS_QUERY,
  ADMIN_CANCEL_BOOKING_MUTATION,
  type AdminBookingSummary,
  type AdminBookingsData,
  type AdminBookingsVariables,
  type AdminCancelBookingData,
  type AdminCancelBookingVariables,
} from '../../graphql/admin'
import { normalizeLocale } from '../../lib/i18n'

const BOOKING_PAGE_SIZE = 12
const STATUSES = [ 'all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled' ] as const

export default function AdminBookingsPage() {
  const { locale, t } = useLang()
  const activeLocale = normalizeLocale(locale)
  const [status, setStatus] = useState<typeof STATUSES[number]>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<AdminBookingSummary | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelErrors, setCancelErrors] = useState<string[]>([])
  const [pageErrors, setPageErrors] = useState<string[]>([])

  const { data, previousData, loading, error, refetch } = useQuery<AdminBookingsData, AdminBookingsVariables>(ADMIN_BOOKINGS_QUERY, {
    variables: {
      status: status === 'all' ? undefined : status,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      search: search || undefined,
      page,
      perPage: BOOKING_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  })

  const [cancelBooking, { loading: cancelling }] = useMutation<AdminCancelBookingData, AdminCancelBookingVariables>(ADMIN_CANCEL_BOOKING_MUTATION)
  const bookingsResult = data?.adminBookings ?? previousData?.adminBookings

  function resetFilters() {
    setStatus('all')
    setDateFrom('')
    setDateTo('')
    setSearchInput('')
    setSearch('')
    setPage(1)
    setExpandedBookingId(null)
  }

  function closeCancelModal() {
    setCancelTarget(null)
    setCancelReason('')
    setCancelErrors([])
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSearch(searchInput.trim())
    setPage(1)
  }

  async function handleCancelConfirm() {
    const trimmedReason = cancelReason.trim()
    if (!cancelTarget) return
    if (!trimmedReason) {
      setCancelErrors([t('Συμπλήρωσε λόγο ακύρωσης.', 'Add a cancellation reason.')])
      return
    }

    setCancelErrors([])
    setPageErrors([])

    try {
      const { data: mutationData } = await cancelBooking({
        variables: {
          bookingId: cancelTarget.id,
          reason: trimmedReason,
        },
      })
      const errors = mutationData?.adminCancelBooking.errors ?? [t('Η ακύρωση απέτυχε.', 'Cancellation failed.')]
      if (errors.length > 0) {
        setCancelErrors(errors)
        return
      }

      closeCancelModal()
      await refetch()
    } catch {
      setCancelErrors([t('Η ακύρωση απέτυχε.', 'Cancellation failed.')])
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">
        <ArrowLeft className="h-4 w-4" />
        {t('Επιστροφή στο dashboard', 'Back to dashboard')}
      </Link>

      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
          {t('Εποπτεία κρατήσεων', 'Booking oversight')}
        </p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
          {t('Όλες οι κρατήσεις με admin controls', 'All bookings with admin controls')}
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
          {t(
            'Φίλτραρε ανά status, εύρος ημερομηνιών ή όνομα πελάτη και παρόχου, άνοιξε inline λεπτομέρειες και ακύρωσε ενεργές κρατήσεις όταν χρειάζεται παρέμβαση.',
            'Filter by status, date range, or customer and provider name, open inline details, and cancel active bookings when intervention is needed.',
          )}
        </p>
      </section>

      <AdminNav />

      <section className="mt-8 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {STATUSES.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => { setStatus(item); setPage(1) }}
                className={[
                  'rounded-full border px-4 py-2 text-sm font-semibold transition',
                  status === item
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700',
                ].join(' ')}
              >
                {item === 'all' ? t('Όλες', 'All') : item.replace('_', ' ')}
              </button>
            ))}
          </div>

          <p className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600">
            {bookingsResult?.totalCount ?? 0} {t('σύνολο', 'total')}
          </p>
        </div>

        <form onSubmit={handleFilterSubmit} className="mt-5 grid gap-3 lg:grid-cols-[1.3fr,0.8fr,0.8fr,auto,auto]">
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('Αναζήτηση πελάτη ή παρόχου', 'Search customer or provider')}
              className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
            />
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => { setDateFrom(event.target.value); setPage(1) }}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(event) => { setDateTo(event.target.value); setPage(1) }}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
          />
          <Button type="submit">{t('Εφαρμογή', 'Apply')}</Button>
          <Button type="button" variant="secondary" onClick={resetFilters}>
            {t('Καθαρισμός', 'Clear')}
          </Button>
        </form>
      </section>

      <div className="mt-6">
        <FormError errors={pageErrors} />
      </div>

      {error ? (
        <div className="mt-6 rounded-[1.5rem] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          <p>{t('Δεν ήταν δυνατή η φόρτωση των κρατήσεων.', 'The bookings list could not be loaded.')}</p>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              {t('Δοκίμασε ξανά', 'Try again')}
            </Button>
          </div>
        </div>
      ) : null}

      {loading && !bookingsResult ? (
        <p className="mt-8 text-sm text-slate-500">{t('Φορτώνουμε τις κρατήσεις...', 'Loading bookings...')}</p>
      ) : (
        <div className="mt-8">
          <BookingTable
            bookings={bookingsResult?.nodes ?? []}
            locale={activeLocale}
            emptyMessage={t('Δεν βρέθηκαν κρατήσεις για αυτά τα φίλτρα.', 'No bookings matched these filters.')}
            expandedBookingId={expandedBookingId}
            cancellingBookingId={cancelling && cancelTarget ? cancelTarget.id : null}
            onToggleExpand={(bookingId) => setExpandedBookingId((current) => current === bookingId ? null : bookingId)}
            onCancelBooking={(booking) => {
              setCancelTarget(booking)
              setCancelReason('')
              setCancelErrors([])
            }}
          />
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {t('Σελίδα', 'Page')} {page}
        </p>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            {t('Προηγούμενη', 'Previous')}
          </Button>
          <Button
            type="button"
            disabled={!bookingsResult?.pageInfo.hasNextPage}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('Επόμενη', 'Next')}
          </Button>
        </div>
      </div>

      {cancelTarget ? (
        <ReasonModal
          title={t('Ακύρωση κράτησης', 'Cancel booking')}
          body={t('Πρόσθεσε λόγο που θα αποθηκευτεί ως admin cancellation reason.', 'Add the reason that will be stored as the admin cancellation reason.')}
          reasonLabel={t('Λόγος ακύρωσης', 'Cancellation reason')}
          reasonPlaceholder={t('Π.χ. Χειροκίνητη admin παρέμβαση.', 'E.g. Manual admin intervention.')}
          confirmLabel={t('Επιβεβαίωση ακύρωσης', 'Confirm cancellation')}
          closeLabel={t('Κλείσιμο', 'Close')}
          loading={cancelling}
          reason={cancelReason}
          errors={cancelErrors}
          onReasonChange={setCancelReason}
          onClose={closeCancelModal}
          onConfirm={() => void handleCancelConfirm()}
        />
      ) : null}
    </div>
  )
}
